import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Storage } from '../lib/storage';

export interface Post {
  id: string;
  usuario_id: string;
  descripcion: string;
  fotos: string[];
  likes_count: number;
  comentarios_count: number;
  created_at: string;
  expires_at: string;
  user_nombre?: string;
  user_avatar?: string;
  has_liked?: boolean;
}

export function useMuro() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMas, setLoadingMas] = useState(false);
  const [pagina, setPagina] = useState(0);
  const [hayMas, setHayMas] = useState(true);
  const [filterActive, setFilterActive] = useState(false);
  const BATCH_SIZE = 10;
  const userId = Storage.get('gravity_user_id');

  const fetchPosts = async (reset = true, filterMine = false, searchStr = '') => {
    try {
      if (reset) {
        setLoading(true);
        setPagina(0);
        setHayMas(true);
        setFilterActive(filterMine);
      } else {
        setLoadingMas(true);
      }

      const currentPage = reset ? 0 : pagina + 1;
      const from = currentPage * BATCH_SIZE;
      const to = (currentPage + 1) * BATCH_SIZE - 1;

      let query = supabase
        .from('t_muro_biker_gravity')
        .select(`
          *,
          usuarios:usuario_id (nombre, avatar_url)
        `)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (filterMine) {
        query = query.eq('usuario_id', userId);
      } else if (searchStr) {
        query = query.ilike('descripcion', `%${searchStr}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Verificar si el usuario actual ha dado like a cada post
      const { data: myLikes } = await supabase
        .from('t_muro_likes_gravity')
        .select('post_id')
        .eq('usuario_id', userId);

      const likedPostIds = new Set(myLikes?.map(l => l.post_id) || []);

      const formattedPosts = (data || []).map((p: any) => ({
        ...p,
        user_nombre: p.usuarios?.nombre || 'Piloto',
        user_avatar: p.usuarios?.avatar_url,
        has_liked: likedPostIds.has(p.id)
      }));

      if (reset) {
        setPosts(formattedPosts);
      } else {
        setPosts(prev => [...prev, ...formattedPosts]);
        setPagina(currentPage);
      }

      if ((data || []).length < BATCH_SIZE) {
        setHayMas(false);
      }

    } catch (err) {
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setLoadingMas(false);
    }
  };

  useEffect(() => {
    if (userId) fetchPosts(true, false, '');
  }, [userId]);

  const toggleLike = async (postId: string) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;

    const newHasLiked = !post.has_liked;
    
    // Actualización optimista
    setPosts(prev => prev.map(p => 
      p.id === postId 
        ? { ...p, has_liked: newHasLiked, likes_count: p.likes_count + (newHasLiked ? 1 : -1) }
        : p
    ));

    try {
      const userStr = Storage.get('gravity_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const pin = user.pin;

      if (!pin) {
        throw new Error('PIN de seguridad no disponible en la sesión. Por favor, cierra sesión y vuelve a ingresar.');
      }

      await supabase.rpc('toggle_like_gravity_seguro', {
        p_post_id: postId,
        p_usuario_id: userId,
        p_pin: pin
      });
    } catch (err: any) {
      console.error('Error toggling like:', err);
      alert(err.message || 'Error al registrar tu reacción.');
      // Revertir si falla
      fetchPosts();
    }
  };

  const checkRestrictions = async () => {
    const userStr = Storage.get('gravity_user');
    if (!userStr) return { canPost: false, reason: 'Inicia sesión' };
    const user = JSON.parse(userStr);
    const isOro = !!user.es_oro;

    // 1. Límite Diario (Se excluyen los Récords de Inercia de Gravity)
    const today = new Date().toISOString().split('T')[0];
    const { count: dailyCount } = await supabase
      .from('t_muro_biker_gravity')
      .select('*', { count: 'exact', head: true })
      .eq('usuario_id', userId)
      .not('descripcion', 'ilike', '%🚀 ¡NUEVO RÉCORD DE INERCIA!%')
      .gte('created_at', today);

    const dailyLimit = isOro ? 3 : 1;
    if ((dailyCount || 0) >= dailyLimit) {
      return { canPost: false, reason: `Has alcanzado tu límite diario de ${dailyLimit} post${dailyLimit > 1 ? 's' : ''}.` };
    }

    // 2. Límite Activos
    const { count: activeCount } = await supabase
      .from('t_muro_biker_gravity')
      .select('*', { count: 'exact', head: true })
      .eq('usuario_id', userId);

    const activeLimit = isOro ? 10 : 3;
    if ((activeCount || 0) >= activeLimit) {
      return { canPost: false, reason: `Has alcanzado tu límite total de ${activeLimit} posts activos. Borra uno para publicar.` };
    }

    // 3. Cooldown (1 hora) (Se excluyen los Récords de Inercia de Gravity)
    const oneHourAgo = new Date(Date.now() - 3600000).toISOString();
    const { count: recentCount } = await supabase
      .from('t_muro_biker_gravity')
      .select('*', { count: 'exact', head: true })
      .eq('usuario_id', userId)
      .not('descripcion', 'ilike', '%🚀 ¡NUEVO RÉCORD DE INERCIA!%')
      .gte('created_at', oneHourAgo);

    if ((recentCount || 0) > 0) {
      return { canPost: true }; // Se deja pasar por consistencia
    }

    return { canPost: true };
  };

  const publicarPost = async (descripcion: string, fotos: string[]) => {
    try {
      const userStr = Storage.get('gravity_user');
      if (!userStr) throw new Error('Usuario no encontrado');
      
      const user = JSON.parse(userStr);
      const pin = user.pin; // Recuperamos el PIN guardado en el login

      if (!pin) throw new Error('PIN de seguridad no disponible en la sesión. Por favor, cierra sesión y vuelve a ingresar.');

      const { data, error } = await supabase.rpc('publicar_en_muro_gravity_seguro', {
        p_usuario_id: userId,
        p_pin: pin,
        p_descripcion: descripcion,
        p_fotos: fotos
      });

      if (error) throw error;
      return { success: true, data };
    } catch (err: any) {
      console.error('Error en publicarPost:', err);
      return { success: false, error: err.message };
    }
  };

  const eliminarPost = async (postId: string) => {
    try {
      const userStr = Storage.get('gravity_user');
      if (!userStr) throw new Error('Usuario no encontrado');
      
      const user = JSON.parse(userStr);
      const pin = user.pin;

      if (!pin) throw new Error('PIN de seguridad no disponible en la sesión. Por favor, cierra sesión y vuelve a ingresar.');

      const { data, error } = await supabase.rpc('eliminar_post_gravity_seguro', {
        p_post_id: postId,
        p_usuario_id: userId,
        p_pin: pin
      });

      if (error) throw error;
      return { success: true, data };
    } catch (err: any) {
      console.error('Error en eliminarPost:', err);
      return { success: false, error: err.message };
    }
  };

  const reportarPost = async (postId: string) => {
    try {
      const userStr = Storage.get('gravity_user');
      const user = userStr ? JSON.parse(userStr) : {};
      const pin = user.pin;

      if (!pin) throw new Error('PIN de seguridad no disponible en la sesión. Por favor, cierra sesión y vuelve a ingresar.');

      const { data: wasDeleted, error } = await supabase.rpc('reportar_post_gravity_seguro', {
        p_post_id: postId,
        p_usuario_id: userId,
        p_pin: pin
      });

      if (error) throw error;
      
      // Si fue borrado (llegó a 3), refrescamos la lista
      if (wasDeleted) {
        fetchPosts();
      }

      return { success: true, wasDeleted };
    } catch (err: any) {
      console.error('Error en reportarPost:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    posts,
    loading,
    refreshing,
    loadingMas,
    hayMas,
    fetchPosts,
    toggleLike,
    checkRestrictions,
    publicarPost,
    eliminarPost,
    reportarPost,
    setRefreshing
  };
}
